<template>
  <VueFinalModal
    content-transition="vfm-fade"
    :click-to-close="true"
    class="flex justify-center items-center p-6"
    content-class="relative w-full max-w-6xl h-[85vh] bg-[#fafbfc] rounded-[40px] shadow-2xl flex overflow-hidden z-50 ring-1 ring-black/5"
  >
    <div class="flex w-full h-full min-h-0">
      <EditBookSidebar
        :background-image-url="sidebarBackgroundUrl"
        :cover-image-url="sidebarCoverUrl"
        :context-badge-text="sidebarBadgeText"
        :context-title="form.name || currentBook?.name"
        :context-subtitle="contextSubtitle"
        :rating="sidebarRating"
        :stats-items="sidebarStatsItems"
        :footer-value="sidebarFooterValue"
      />

      <div class="flex-1 flex flex-col bg-[#fafbfc] relative overflow-hidden min-w-0">
        <header class="px-8 py-6 border-b border-[#1c4848]/5 flex items-center justify-between bg-white/50 backdrop-blur-sm sticky top-0 z-30">
          <div>
            <div class="flex items-center gap-2 text-[#1c4848]/40 mb-1">
              <i class="pr pr-category text-sm block"></i>
              <span class="text-[10px] font-bold uppercase tracking-wider">/ Books / Edit</span>
            </div>
            <h2 class="text-xl font-bold text-[#1c4848] tracking-tight">Update Details</h2>
          </div>
          <div class="flex gap-3">
            <button
              type="button"
              @click="close"
              class="px-4 py-2 rounded-xl text-xs font-bold text-[#1c4848]/60 hover:text-[#1c4848] hover:bg-[#1c4848]/5 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              @click="save"
              class="px-5 py-2 rounded-xl text-xs font-bold text-white bg-[#1c4848] shadow-lg shadow-[#1c4848]/20 hover:bg-[#153838] active:scale-95 transition-all flex items-center gap-2"
            >
              <i class="pr pr-tick-square block"></i>
              Save Changes
            </button>
          </div>
        </header>

        <div class="flex-1 overflow-y-auto p-8 custom-scrollbar">
          <form @submit.prevent="save" class="space-y-8 max-w-3xl mx-auto">
            <section>
              <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4 flex items-center gap-2">
                <i class="pr pr-note-2 text-lg block"></i>
                General Information
              </h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div class="md:col-span-2">
                  <TextInput
                    v-model="form.name"
                    label="Book Title"
                    placeholder="Naziv knjige"
                    input-id="edit-book-title"
                    right-icon-class="pr pr-edit"
                  />
                </div>
                <div>
                  <SelectInput
                    v-model="form.authorId"
                    label="Author"
                    select-id="edit-book-author"
                    :options="authorOptions"
                  />
                </div>
                <div>
                  <SelectInput
                    v-model="form.languageId"
                    label="Language"
                    select-id="edit-book-language"
                    :options="languageOptions"
                  />
                </div>
                <div>
                  <TextInput
                    v-model="form.edition"
                    label="Edition / Category"
                    placeholder="e.g. Seerah"
                    input-id="edit-book-edition"
                    right-icon-class="pr pr-edit"
                  />
                </div>
                <div class="md:col-span-2">
                  <TextareaInput
                    v-model="form.description"
                    label="Description"
                    placeholder="Opis knjige"
                    textarea-id="edit-book-description"
                    :rows="4"
                  />
                </div>
              </div>
            </section>

            <section>
              <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4 flex items-center gap-2">
                <i class="pr pr-gallery text-lg block"></i>
                Cover Image
              </h4>
              <ImageDropzone
                v-model="form.coverImageFile"
                input-id="edit-book-cover"
                :initial-preview-url="currentBook?.coverImage?.url ?? currentBook?.cover_image ?? currentBook?.image ?? currentBook?.cover_image_url"
                :title-text="coverDropzoneTitle"
                :hint-text="coverDropzoneHint"
              />
              <div v-if="sidebarCoverUrl" class="mt-4 flex flex-wrap items-center gap-4">
                <div class="aspect-[5/4] w-40 shrink-0 overflow-hidden rounded-2xl bg-[#f2f4f4] ring-1 ring-[#1c4848]/10">
                  <img :src="sidebarCoverUrl" alt="How the cover is cropped in the app's library" class="h-full w-full object-cover" :style="{ objectPosition: `center ${form.coverPosition}` }">
                </div>
                <div class="min-w-0">
                  <FormLabel text="Part of the cover shown in the library" for-id="edit-book-cover-position" />
                  <div id="edit-book-cover-position" class="flex gap-1" role="group" aria-label="Part of the cover shown in the library">
                    <button v-for="option in COVER_POSITIONS" :key="option.value" type="button" class="min-h-9 rounded-lg px-3 text-xs font-bold transition-colors" :class="form.coverPosition === option.value ? 'bg-[#1c4848] text-white' : 'bg-[#f2f4f4] text-[#1c4848]/70 hover:bg-[#1c4848]/10'" :aria-pressed="form.coverPosition === option.value" @click="form.coverPosition = option.value">{{ option.label }}</button>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4 flex items-center gap-2">
                <i class="pr pr-more text-lg block"></i>
                Configuration
              </h4>
              <div class="bg-white rounded-3xl p-6 border border-[#1c4848]/5 shadow-sm space-y-6">
                <div class="grid grid-cols-2 md:grid-cols-3 gap-4 pb-6 border-b border-[#1c4848]/5">
                  <div>
                    <FormLabel text="ISBN" for-id="edit-book-isbn" />
                    <input
                      id="edit-book-isbn"
                      v-model="form.isbn"
                      type="text"
                      class="w-full bg-[#f2f4f4] border-none rounded-xl px-3 py-2 text-xs font-bold text-[#1c4848] focus:ring-2 focus:ring-[#1c4848]/10 focus:outline-none"
                    />
                  </div>
                  <div>
                    <FormLabel text="Pages (count)" for-id="edit-book-pages-display" />
                    <input
                      id="edit-book-pages-display"
                      :value="bookTotalPagesDisplay"
                      type="text"
                      readonly
                      class="w-full bg-[#f2f4f4] border-none rounded-xl px-3 py-2 text-xs font-bold text-[#1c4848]/60 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <FormLabel text="Status" for-id="edit-book-status" />
                    <select
                      id="edit-book-status"
                      v-model="form.status"
                      class="w-full bg-[#e1f5e8] text-emerald-700 border-none rounded-xl px-3 py-2 text-xs font-bold focus:ring-2 focus:ring-emerald-500/20 cursor-pointer focus:outline-none"
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="IN_PROGRESS">In progress</option>
                      <option value="TESTING">Test phones only</option>
                      <option value="INACTIVE">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <FormLabel text="Audience" for-id="edit-book-audience" />
                    <select
                      id="edit-book-audience"
                      v-model="form.audience"
                      class="w-full bg-[#f2f4f4] border-none rounded-xl px-3 py-2 text-xs font-bold text-[#1c4848] focus:ring-2 focus:ring-[#1c4848]/10 cursor-pointer focus:outline-none"
                    >
                      <option value="adult">Adults</option>
                      <option value="kids">Kids</option>
                    </select>
                  </div>
                </div>
                <div class="flex flex-col md:flex-row gap-6 md:gap-10">
                  <ToggleInput
                    v-model="form.isPremium"
                    title="Premium Content"
                    description="Require subscription"
                  />
                  <ToggleInput
                    v-model="form.isPublished"
                    title="Publish Now"
                    description="Visible in app"
                  />
                  <ToggleInput
                    v-model="form.allowComments"
                    title="Comments"
                    description="Allow user reviews"
                  />
                </div>
              </div>
            </section>

            <section>
              <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4 flex items-center gap-2">
                <i class="pr pr-note-2 text-lg block"></i>
                Publishing
              </h4>
              <div class="bg-white rounded-3xl p-6 border border-[#1c4848]/5 shadow-sm space-y-5">
                <div>
                  <FormLabel text="Publication date" for-id="edit-book-publication-date" />
                  <input
                    id="edit-book-publication-date"
                    v-model="form.publicationDate"
                    type="date"
                    class="w-full bg-[#f2f4f4] border-none rounded-xl px-3 py-2 text-xs font-bold text-[#1c4848] focus:ring-2 focus:ring-[#1c4848]/10 focus:outline-none"
                  />
                </div>
                <div>
                  <FormLabel text="References" for-id="edit-book-references" />
                  <textarea
                    id="edit-book-references"
                    v-model="form.references"
                    rows="2"
                    class="w-full bg-[#f2f4f4] border-none rounded-xl px-3 py-2 text-xs font-bold text-[#1c4848] focus:ring-2 focus:ring-[#1c4848]/10 focus:outline-none resize-none"
                  />
                </div>
              </div>
            </section>

            <section>
              <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4 flex items-center gap-2">
                <i class="pr pr-more text-lg block"></i>
                Stats &amp; moderation
              </h4>
              <div class="bg-white rounded-3xl p-6 border border-[#1c4848]/5 shadow-sm space-y-5">
                <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <FormLabel text="View count" for-id="edit-book-view-count" />
                    <input
                      id="edit-book-view-count"
                      :value="form.viewCount"
                      type="text"
                      readonly
                      class="w-full bg-[#f2f4f4] border-none rounded-xl px-3 py-2 text-xs font-bold text-[#1c4848]/60 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <FormLabel text="Rating" for-id="edit-book-rating" />
                    <input
                      id="edit-book-rating"
                      :value="form.rating"
                      type="text"
                      readonly
                      class="w-full bg-[#f2f4f4] border-none rounded-xl px-3 py-2 text-xs font-bold text-[#1c4848]/60 cursor-not-allowed"
                    />
                  </div>
                  <div class="md:col-span-2">
                    <FormLabel text="Approval status" for-id="edit-book-approval-status" />
                    <select
                      id="edit-book-approval-status"
                      v-model="form.approvalStatus"
                      class="w-full bg-[#f2f4f4] border-none rounded-xl px-3 py-2 text-xs font-bold text-[#1c4848] focus:ring-2 focus:ring-[#1c4848]/10 cursor-pointer focus:outline-none"
                    >
                      <option value="DISAPPROVED">Disapproved</option>
                      <option value="PENDING">Pending</option>
                      <option value="APPROVED">Approved</option>
                    </select>
                  </div>
                </div>
                <div>
                  <FormLabel text="Notes" for-id="edit-book-notes" />
                  <textarea
                    id="edit-book-notes"
                    v-model="form.notes"
                    rows="2"
                    class="w-full bg-[#f2f4f4] border-none rounded-xl px-3 py-2 text-xs font-bold text-[#1c4848] focus:ring-2 focus:ring-[#1c4848]/10 focus:outline-none resize-none"
                  />
                </div>
              </div>
            </section>

            <section class="pb-6">
              <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4 flex items-center gap-2">
                <i class="pr pr-note-2 text-lg block"></i>
                Tags
              </h4>
              <div class="bg-white rounded-3xl p-6 border border-[#1c4848]/5 shadow-sm">
                <FormLabel text="Tags (comma-separated)" for-id="edit-book-tags" />
                <input
                  id="edit-book-tags"
                  v-model="form.tagsText"
                  type="text"
                  placeholder="tag1, tag2, tag3"
                  class="w-full bg-[#f2f4f4] border-none rounded-xl px-3 py-2 text-xs font-bold text-[#1c4848] focus:ring-2 focus:ring-[#1c4848]/10 focus:outline-none mt-1.5"
                />
              </div>
            </section>
          </form>
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { VueFinalModal } from 'vue-final-modal';
import { useBooksStore } from '@/store/books/index.js';
import { useAuthorsStore } from '@/store/authors/index.js';
import { useLanguagesStore } from '@/store/languages/index.js';
import { getLanguageLabel } from '@/utils/languageLabel.js';
import EditBookSidebar from '@/components/book-modal/EditBookSidebar.vue';
import TextInput from '@/components/forms/text/TextInput.vue';
import SelectInput from '@/components/forms/select/SelectInput.vue';
import TextareaInput from '@/components/forms/textarea/TextareaInput.vue';
import ImageDropzone from '@/components/forms/dropzone/ImageDropzone.vue';
import ToggleInput from '@/components/forms/toggle/ToggleInput.vue';
import FormLabel from '@/components/forms/Label.vue';

const props = defineProps({
  book: { type: Object, default: null }
});

const emit = defineEmits(['confirm', 'close']);

const booksStore = useBooksStore();
const authorsStore = useAuthorsStore();
const languagesStore = useLanguagesStore();

const defaultBackgroundUrl = 'https://fra1.digitaloceanspaces.com/aplusspace/69/12c93ede1d5bbc246b9022e680238d18c1d0c2ec.png';

const loadedBook = ref(null);
const form = ref({
  name: '',
  description: '',
  authorId: '',
  authorLabel: '',
  edition: '',
  coverImageFile: null,
  coverPosition: 'center',
  isbn: '',
  status: 'ACTIVE',
  audience: 'adult',
  isPremium: false,
  isPublished: false,
  allowComments: false,
  publicationDate: '',
  references: '',
  viewCount: 0,
  rating: 0,
  approvalStatus: 'PENDING',
  notes: '',
  tagsText: '',
  languageId: ''
});

const authorOptions = computed(() =>
  authorsStore.authors.map((a) => ({
    value: String(a.id),
    label: a.name || a.nickname || ''
  }))
);

const languageOptions = computed(() => {
  const list = languagesStore.languages ?? [];
  const opts = list.map((l) => ({ value: String(l.id), label: getLanguageLabel(l) }));
  return [{ value: '', label: 'Select language' }, ...opts];
});

const currentBook = computed(() => loadedBook.value ?? props.book);
const sidebarBackgroundUrl = computed(() => defaultBackgroundUrl);
const COVER_POSITIONS = [{ value: 'top', label: 'Top' }, { value: 'center', label: 'Middle' }, { value: 'bottom', label: 'Bottom' }];

const sidebarCoverUrl = computed(() => {
  if (form.value.coverImageFile instanceof File) return URL.createObjectURL(form.value.coverImageFile);
  const b = currentBook.value;
  return b?.coverImage?.url ?? b?.cover_image ?? b?.image ?? b?.cover_image_url ?? '';
});
const sidebarBadgeText = computed(() => (currentBook.value?.id ? `Active ID: ${String(currentBook.value.id).padStart(3, '0')}` : ''));
const sidebarRating = computed(() => currentBook.value?.rating ?? '4.9');
const sidebarStatsItems = computed(() => {
  const b = currentBook.value;
  return [
    { iconClass: 'pr pr-eye', value: b?.viewCount ?? b?.view_count ?? '0', label: 'Total Views' },
    { iconClass: 'pr pr-bookmark', value: b?.totalPages ?? b?.total_pages ?? '0', label: 'Pages' }
  ];
});
const sidebarFooterValue = computed(() => {
  const b = currentBook.value;
  const at = b?.updatedAt ?? b?.updated_at;
  return at ?? '—';
});
const contextSubtitle = computed(
  () => authorOptions.value.find(o => o.value === form.value.authorId)?.label ?? form.value.authorLabel ?? ''
);
const bookTotalPagesDisplay = computed(() => {
  const b = currentBook.value;
  const v = b?.totalPages ?? b?.total_pages;
  return v != null ? String(v) : '—';
});
const coverDropzoneTitle = computed(() => (form.value.coverImageFile || currentBook.value?.coverImage?.url ? 'Click to replace cover' : 'Click to upload cover'));
const coverDropzoneHint = computed(() => 'SVG, PNG, JPG or GIF (max. 2MB)');

function close() {
  emit('close');
  emit('confirm');
}

function prepopulate(b) {
  if (!b) return;
  form.value.name = b.name ?? '';
  form.value.description = b.description ?? '';
  form.value.isbn = b.isbn ?? '';
  const statusVal = b.status ?? 'ACTIVE';
  form.value.status = ['ACTIVE', 'IN_PROGRESS', 'INACTIVE', 'TESTING'].includes(statusVal) ? statusVal : 'ACTIVE';
  form.value.coverPosition = ['top', 'center', 'bottom'].includes(b.coverPosition) ? b.coverPosition : 'center';
  const audienceVal = (b.audience ?? '').toString().toLowerCase();
  form.value.audience = ['adult', 'kids'].includes(audienceVal) ? audienceVal : 'adult';
  form.value.isPremium = b.isPremium ?? b.is_premium ?? false;
  form.value.isPublished = b.isPublished ?? b.is_published ?? false;
  form.value.allowComments = b.commentsEnabled ?? b.comments_enabled ?? false;
  const authorId = b.author_id ?? b.author?.id;
  form.value.authorId = authorId != null ? String(authorId) : '1';
  form.value.authorLabel = b.author?.name ?? b.author?.nickname ?? b.author_name ?? authorOptions.value.find(o => o.value === form.value.authorId)?.label ?? '';
  form.value.edition = b.edition ?? '';
  form.value.publicationDate = b.publicationDate ?? b.publication_date ?? '';
  form.value.references = b.references ?? '';
  form.value.viewCount = b.viewCount ?? b.view_count ?? 0;
  form.value.rating = b.rating ?? 0;
  const approval = b.approvalStatus ?? b.approval_status ?? 'PENDING';
  form.value.approvalStatus = ['DISAPPROVED', 'PENDING', 'APPROVED'].includes(approval) ? approval : 'PENDING';
  form.value.notes = b.notes ?? '';
  const langId = b.language_id ?? b.languageId ?? b.language?.id;
  form.value.languageId = langId != null ? String(langId) : '';
  const tagsArr = b.tags;
  form.value.tagsText = Array.isArray(tagsArr) ? tagsArr.map(t => (typeof t === 'string' ? t : t?.name)).filter(Boolean).join(', ') : '';
}

async function loadBook() {
  if (!props.book?.id) return;
  loadedBook.value = null;
  try {
    await Promise.all([authorsStore.fetchAuthors(), languagesStore.fetchLanguages()]);
    const data = await booksStore.fetchBook(props.book.id, 'author,tags,coverImage');
    loadedBook.value = data;
    prepopulate(data);
  } catch (err) {
    console.error(err);
    prepopulate(props.book);
  }
}

async function save() {
  const tags = form.value.tagsText
    ? form.value.tagsText.split(',').map(s => s.trim()).filter(Boolean)
    : undefined;
  const approval = form.value.approvalStatus;
  const payload = {
    name: form.value.name,
    description: form.value.description || undefined,
    author_id: form.value.authorId ? Number(form.value.authorId) : undefined,
    language_id: form.value.languageId ? Number(form.value.languageId) : null,
    status: form.value.status,
    cover_position: form.value.coverPosition,
    audience: ['adult', 'kids'].includes(form.value.audience) ? form.value.audience : undefined,
    edition: form.value.edition || undefined,
    isbn: form.value.isbn || undefined,
    is_premium: form.value.isPremium,
    is_published: form.value.isPublished,
    comments_enabled: form.value.allowComments,
    publication_date: form.value.publicationDate || undefined,
    references: form.value.references || undefined,
    approval_status: ['DISAPPROVED', 'PENDING', 'APPROVED'].includes(approval) ? approval : undefined,
    notes: form.value.notes || undefined,
    tags
  };

  const bookId = props.book?.id ?? loadedBook.value?.id;

  try {
    if (!bookId) {
      if (!payload.name?.trim()) return;
      if (!payload.author_id) return;
      const created = await booksStore.createBook(payload);
      const newId = created?.id ?? created?.data?.id;
      if (newId && form.value.coverImageFile instanceof File) {
        await booksStore.uploadBookCover(newId, form.value.coverImageFile);
      }
      await booksStore.fetchBooks();
      close();
      return;
    }

    await booksStore.updateBook(bookId, payload);
    if (form.value.coverImageFile instanceof File) {
      await booksStore.uploadBookCover(bookId, form.value.coverImageFile);
    }
    close();
  } catch (err) {
    console.error(err);
  }
}

watch(() => props.book?.id, async (id) => {
  await Promise.all([authorsStore.fetchAuthors(), languagesStore.fetchLanguages()]);
  if (id) loadBook();
  else {
    loadedBook.value = null;
    prepopulate(props.book ?? {});
  }
}, { immediate: true });
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(28, 72, 72, 0.2);
  border-radius: 3px;
}
</style>
